import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequest } from '../../hooks/useRequest';
import { useAppDispatch } from '../../store/hooks';
import { refreshChats } from '../../store/slices/chats-slice';
import { deleteAllChats, regenerateAuthToken } from '../../utils/axios/api';
import { PatPage } from './pat.page';

export function TokensPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const regenerateRequest = useRequest<{ token: string }>();
  const deleteAllChatsRequest = useRequest();
  const [authToken, setAuthToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [isDeleteChatsDialogOpen, setIsDeleteChatsDialogOpen] = useState(false);

  const handleRegenerate = useCallback(async () => {
    setCopyError(null);
    setCopied(false);

    const result = await regenerateRequest.fetch(() => regenerateAuthToken());
    if (!result) {
      return;
    }
    setAuthToken(result.token);
  }, [regenerateRequest]);

  const handleCopy = useCallback(async () => {
    if (!authToken) {
      return;
    }

    try {
      await navigator.clipboard.writeText(authToken);
      setCopied(true);
      setCopyError(null);
    } catch {
      setCopyError(t('settings.authToken.errors.copyFailed'));
    }
  }, [authToken, t]);

  const handleDeleteAllChats = useCallback(async () => {
    const result = await deleteAllChatsRequest.fetch(async () => {
      await deleteAllChats();
      await dispatch(refreshChats()).unwrap();
    });

    if (result === null) {
      return;
    }

    setIsDeleteChatsDialogOpen(false);
  }, [deleteAllChatsRequest, dispatch]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="subtitle1">
          {t('settings.authToken.label')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('settings.authToken.hint')}
        </Typography>
        <TextField
          value={authToken}
          placeholder={t('settings.authToken.empty')}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <Tooltip
                  title={
                    copied
                      ? t('settings.authToken.copied')
                      : t('settings.authToken.copy')
                  }
                >
                  <span>
                    <IconButton
                      aria-label={t('settings.authToken.copy')}
                      onClick={() => {
                        void handleCopy();
                      }}
                      disabled={!authToken}
                    >
                      <ContentCopyOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ),
            },
          }}
          error={Boolean(copyError ?? regenerateRequest.exception)}
          helperText={
            copyError ??
            (regenerateRequest.exception
              ? t(`exceptions.${regenerateRequest.exception}`)
              : undefined)
          }
          fullWidth
        />
        <Button
          variant="contained"
          onClick={() => {
            void handleRegenerate();
          }}
          disabled={regenerateRequest.isLoading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('settings.authToken.generate')}
        </Button>
      </Box>
      <PatPage />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="subtitle1">
          {t('settings.chats.label')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('settings.chats.hint')}
        </Typography>
        {deleteAllChatsRequest.exception ? (
          <Typography variant="body2" color="error">
            {t(`exceptions.${deleteAllChatsRequest.exception}`)}
          </Typography>
        ) : null}
        <Button
          color="error"
          variant="outlined"
          onClick={() => setIsDeleteChatsDialogOpen(true)}
          disabled={deleteAllChatsRequest.isLoading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('settings.chats.delete.button')}
        </Button>
      </Box>
      <Dialog
        open={isDeleteChatsDialogOpen}
        onClose={() => {
          if (!deleteAllChatsRequest.isLoading) {
            setIsDeleteChatsDialogOpen(false);
          }
        }}
      >
        <DialogTitle>{t('settings.chats.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('settings.chats.delete.confirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteChatsDialogOpen(false)}
            disabled={deleteAllChatsRequest.isLoading}
          >
            {t('settings.chats.delete.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteAllChatsRequest.isLoading}
            onClick={() => {
              void handleDeleteAllChats();
            }}
          >
            {deleteAllChatsRequest.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('settings.chats.delete.button')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
